import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import certificationsApi from '@/api/certificationsApi';
import type { ApiResponse, CertificationModel } from '@/api/types';

type CertificationPayload = Record<string, unknown>;
type CertificationListResponse = ApiResponse<CertificationModel[]>;
type CertificationResponse = ApiResponse<CertificationModel>;

// Query keys for certification-related queries
export const CERTIFICATIONS_KEYS = {
  all: ['certifications'] as const,
  byId: (id: string) => ['certifications', id] as const,
  byStatus: (status: string) => ['certifications', 'status', status] as const,
};

/**
 * Hook for fetching all certifications
 */
export function useCertifications(
  options: ApiQueryOptions<CertificationListResponse> = {},
) {
  return useAuthenticatedQuery<CertificationListResponse>(
    CERTIFICATIONS_KEYS.all,
    token => certificationsApi.getCertifications(token),
    options,
  );
}

/**
 * Hook for fetching a specific certification by ID
 */
export function useCertification(
  id: string,
  options: ApiQueryOptions<CertificationResponse> = {},
) {
  return useAuthenticatedQuery<CertificationResponse>(
    CERTIFICATIONS_KEYS.byId(id),
    token => certificationsApi.getCertificationById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for creating a new certification request
 */
export function useCreateCertification(
  options: ApiMutationOptions<
    CertificationResponse,
    { certificationData: CertificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { certificationData: CertificationPayload }
  >(
    ({ certificationData, token }) =>
      certificationsApi.createCertification(certificationData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for updating a certification request
 */
export function useUpdateCertification(
  options: ApiMutationOptions<
    CertificationResponse,
    { id: string; certificationData: CertificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { id: string; certificationData: CertificationPayload }
  >(
    ({ id, certificationData, token }) =>
      certificationsApi.updateCertification(id, certificationData, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CERTIFICATIONS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting certification feedback
 */
export function useSubmitCertificationFeedback(
  options: ApiMutationOptions<
    CertificationResponse,
    { feedbackData: CertificationPayload & { certificate_request_id?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { feedbackData: CertificationPayload & { certificate_request_id?: string } }
  >(
    ({ feedbackData, token }) =>
      certificationsApi.submitFeedback(feedbackData, token),
    {
      onSuccess: (_data, variables) => {
        if (variables.feedbackData.certificate_request_id) {
          queryClient.invalidateQueries({
            queryKey: CERTIFICATIONS_KEYS.byId(
              variables.feedbackData.certificate_request_id,
            ),
          });
        }
      },
      ...options,
    },
  );
}

/**
 * Hook for marking certification as incomplete
 */
export function useMarkCertificationIncomplete(
  options: ApiMutationOptions<
    CertificationResponse,
    { requestData: CertificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { requestData: CertificationPayload }
  >(
    ({ requestData, token }) =>
      certificationsApi.markAsIncomplete(requestData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for accepting certification payment
 */
export function useAcceptCertification(
  options: ApiMutationOptions<
    CertificationResponse,
    { certificationId: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { certificationId: string }
  >(
    ({ certificationId, token }) =>
      certificationsApi.acceptCertification(certificationId, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CERTIFICATIONS_KEYS.byId(variables.certificationId),
        });
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting cancellation comment
 */
export function useSubmitCertificationCancellation(
  options: ApiMutationOptions<
    CertificationResponse,
    { certificateId: string; comment: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { certificateId: string; comment: string }
  >(
    ({ certificateId, comment, token }) =>
      certificationsApi.submitCancellationComment(
        certificateId,
        comment,
        token,
      ),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: CERTIFICATIONS_KEYS.byId(variables.certificateId),
        });
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting certification invoice (facture)
 */
export function useSubmitCertificationFacture(
  options: ApiMutationOptions<
    CertificationResponse,
    { factureData: CertificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { factureData: CertificationPayload }
  >(
    ({ factureData, token }) =>
      certificationsApi.submitFacture(factureData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for completing a certification request
 */
export function useCompleteCertificationRequest(
  options: ApiMutationOptions<
    CertificationResponse,
    { completionData: CertificationPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { completionData: CertificationPayload }
  >(
    ({ completionData, token }) =>
      certificationsApi.completeRequest(completionData, token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for checking calculation items
 */
export function useCheckCertificationCalculation(
  options: ApiMutationOptions<
    CertificationResponse,
    { calculationId: string; items: unknown }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    CertificationResponse,
    { calculationId: string; items: unknown }
  >(
    ({ calculationId, items, token }) =>
      certificationsApi.checkCalculation(calculationId, items, token),
    {
      onSuccess: () => {
        // Invalidate all certifications as the calculation affects the certification status
        queryClient.invalidateQueries({ queryKey: CERTIFICATIONS_KEYS.all });
      },
      ...options,
    },
  );
}
